import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import AOS from 'aos';
import {
  FaArrowRight, FaRocket, FaUsers, FaCode,
  FaGraduationCap, FaLightbulb, FaHandshake,
  FaGlobe, FaAward, FaHeart, FaBriefcase,
  FaCheckCircle, FaLayerGroup, FaDatabase, FaCloud
} from 'react-icons/fa';
import './About.css';

const TIMELINE = [
  { year: '2022', label: 'Founded', desc: 'Vkomak Technologies established in Chennai on Valentine\'s Day — Feb 14, 2022' },
  { year: '2022', label: 'First Product', desc: 'Launched SIKGEN AI platform and secured our first enterprise clients' },
  { year: '2023', label: 'Team Growth', desc: 'Expanded to 12+ full-time developers and launched TalentX training division' },
  { year: '2023', label: 'PromoX Launch', desc: 'Launched PromoX digital marketing division and TalentForce staffing unit' },
  { year: '2024', label: '8+ Products', desc: 'Expanded product portfolio to 8+ live SaaS products across industries' },
  { year: '2025', label: 'Global Reach', desc: 'Extended operations globally with 33+ placed professionals and 5+ enterprise clients' },
];

const VALUES = [
  { icon: <FaLightbulb />, title: 'Innovation First', desc: 'We push boundaries with AI, automation, and next-gen technology.', color: '#7c3aed' },
  { icon: <FaHandshake />, title: 'Client Partnership', desc: 'We are not vendors — we are long-term growth partners.', color: '#1e40af' },
  { icon: <FaAward />, title: 'Excellence Always', desc: 'Every line of code and every delivery is done with pride and precision.', color: '#0891b2' },
  { icon: <FaGlobe />, title: 'Global Thinking', desc: 'We build products and strategies for the global marketplace.', color: '#059669' },
  { icon: <FaHeart />, title: 'People First', desc: 'Our team\'s growth and well-being drive everything we do.', color: '#dc2626' },
  { icon: <FaBriefcase />, title: 'Business Impact', desc: 'Every solution is designed to deliver measurable ROI.', color: '#d97706' },
];


const CORE_STRENGTHS = [
  { icon: <FaCode />, title: 'Enterprise Software Development', color: '#1e40af' },
  { icon: <FaRocket />, title: 'AI & Data-Driven Solutions', color: '#7c3aed' },
  { icon: <FaCloud />, title: 'Cloud & Scalable Architecture', color: '#0891b2' },
  { icon: <FaLayerGroup />, title: 'UI/UX Focused Design', color: '#059669' },
  { icon: <FaDatabase />, title: 'End-to-End Product Engineering', color: '#d97706' },
];

const WHY_STATS = [
  { value: '11+', label: 'Years Industry Experience', color: '#1e40af' },
  { value: '5+', label: 'Live Global Clients', color: '#0891b2' },
  { value: '30+', label: 'Training & Dev Projects', color: '#7c3aed' },
  { value: '100%', label: 'Scalable & Secure Architecture', color: '#059669' },
];

const About = () => {
  useEffect(() => { AOS.init({ duration: 900, once: true, offset: 80 }); }, []);

  return (
    <div className="about-page">

      {/* Page Hero */}
      <section className="page-hero">
        <div className="page-hero-overlay" />
        <Container>
          <div className="page-hero-content" data-aos="fade-up">
            <div className="breadcrumb-vk">
              <Link to="/">Home</Link> / <span>About Us</span>
            </div>
            <h1 className="page-hero-title">
              Building the Future of<br />
              <span>Digital Business</span>
            </h1>
            <p className="page-hero-desc">
              We're not just a technology company — we're a digital transformation partner committed to delivering scalable, secure, and intelligent enterprise solutions that empower businesses to thrive in the digital era.
            </p>
          </div>
        </Container>
      </section>

      {/* Story */}
      <section className="about-story section-py">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6} data-aos="fade-right">
              <span className="section-badge">Our Story</span>
              <h2 className="section-title">
                Born in Chennai,<br />
                <span>Building for the World</span>
              </h2>
              <div className="gradient-line" />
              <p className="section-subtitle mb-3">
                Founded on February 14, 2022, Vkomak Technologies started with a simple but bold vision — to build world-class software products that solve real business problems at scale.
              </p>
              <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.75, marginBottom: '1rem' }}>
                Vkomak Technologies is a SaaS and service-based technology company focused on delivering scalable, secure, and intelligent business solutions. With expertise in enterprise systems, AI integration, and modern web technologies, we help startups, SMEs, and global enterprises transform digitally.
              </p>
              <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.75, marginBottom: '1.5rem' }}>
                Our stack of choice — <strong>.NET Core, Angular, React, PostgreSQL, SQL Server, AWS, and Azure</strong> — powers every product we build, ensuring enterprise reliability and cloud-native scalability.
              </p>
              <div className="about-strengths-grid">
                {CORE_STRENGTHS.map((s, i) => (
                  <div key={i} className="about-strength-item" style={{ '--s-color': s.color }}>
                    <div className="about-strength-icon" style={{ color: s.color, background: `${s.color}12` }}>{s.icon}</div>
                    <span>{s.title}</span>
                  </div>
                ))}
              </div>
            </Col>
            <Col lg={6} data-aos="fade-left">
              <div className="story-metrics-grid">
                {[
                  { value: '2022', label: 'Founded', icon: <FaRocket />, color: '#1e40af' },
                  { value: '5+', label: 'Global Clients', icon: <FaUsers />, color: '#0891b2' },
                  { value: '8+', label: 'SaaS Products', icon: <FaCode />, color: '#7c3aed' },
                  { value: '4', label: 'Divisions', icon: <FaBriefcase />, color: '#059669' },
                  { value: '30+', label: 'Dev Projects', icon: <FaGraduationCap />, color: '#d97706' },
                  { value: 'Chennai', label: 'Headquarters', icon: <FaGlobe />, color: '#dc2626' },
                ].map((m, i) => (
                  <div key={i} className="story-metric-card" data-aos="zoom-in" data-aos-delay={i * 80}>
                    <div className="story-metric-icon" style={{ color: m.color, background: `${m.color}12` }}>
                      {m.icon}
                    </div>
                    <div className="story-metric-value">{m.value}</div>
                    <div className="story-metric-label">{m.label}</div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Why Choose Vkomak */}
      <section className="about-why section-py" style={{ background: 'var(--color-bg)' }}>
        <Container>
          <div className="section-header text-center" data-aos="fade-up">
            <span className="section-badge">Why Choose Vkomak</span>
            <h2 className="section-title">Numbers That <span>Speak for Us</span></h2>
          </div>
          <div className="about-why-grid">
            {WHY_STATS.map((s, i) => (
              <div key={i} className="about-why-card" data-aos="zoom-in" data-aos-delay={i * 80}>
                <div className="about-why-value" style={{ color: s.color }}>{s.value}</div>
                <div className="about-why-label"><FaCheckCircle style={{ color: s.color, marginRight: 6 }} />{s.label}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Vision & Mission */}
      <section className="vm-section section-py">
        <Container>
          <Row className="g-4">
            <Col md={6} data-aos="fade-right">
              <div className="vm-card vision">
                <div className="vm-icon">👁️</div>
                <h3>Our Vision</h3>
                <p>
                  To become a <strong>globally trusted technology group</strong> delivering cutting-edge software innovation, real-time talent development, intelligent digital marketing, and strategic IT consulting — empowering businesses, professionals, and communities to thrive in the era of AI, automation, and digital transformation.
                </p>
              </div>
            </Col>
            <Col md={6} data-aos="fade-left">
              <div className="vm-card mission">
                <div className="vm-icon">🎯</div>
                <h3>Our Mission</h3>
                <p>
                  To engineer <strong>scalable, secure, and intelligent software products</strong> that solve complex real-world problems and accelerate digital transformation. We are committed to delivering excellence through innovation, customer-centric solutions, and continuous improvement.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Values */}
      <section className="values-section section-py">
        <Container>
          <div className="section-header text-center" data-aos="fade-up">
            <span className="section-badge">Our Values</span>
            <h2 className="section-title">What Drives <span>Everything We Do</span></h2>
          </div>
          <Row className="g-4">
            {VALUES.map((v, i) => (
              <Col lg={4} md={6} key={i} data-aos="fade-up" data-aos-delay={i * 80}>
                <div className="value-card">
                  <div className="value-icon" style={{ background: `${v.color}12`, color: v.color }}>{v.icon}</div>
                  <h4 className="value-title">{v.title}</h4>
                  <p className="value-desc">{v.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Journey Timeline */}
      <section className="timeline-section section-py">
        <Container>
          <div className="section-header text-center" data-aos="fade-up">
            <span className="section-badge">Our Journey</span>
            <h2 className="section-title">From Startup to <span>Technology Group</span></h2>
          </div>
          <div className="timeline">
            {TIMELINE.map((item, i) => (
              <div key={i} className={`timeline-item ${i % 2 === 0 ? 'left' : 'right'}`} data-aos={i % 2 === 0 ? 'fade-right' : 'fade-left'}>
                <div className="timeline-content">
                  <span className="timeline-year">{item.year}</span>
                  <h4 className="timeline-label">{item.label}</h4>
                  <p className="timeline-desc">{item.desc}</p>
                </div>
                <div className="timeline-dot" />
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="about-cta section-py">
        <Container className="text-center" data-aos="zoom-in">
          <h2 className="section-title mb-3">
            Ready to Work with <span>Vkomak?</span>
          </h2>
          <p className="section-subtitle mb-4" style={{ margin: '0 auto 2rem' }}>
            Let's build your next digital product together.
          </p>
          <Link to="/contact" className="btn-primary-vk">
            Contact Us <FaArrowRight />
          </Link>
        </Container>
      </section>

    </div>
  );
};

export default About;
